import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { Injector, isDevMode } from '@angular/core';
import { AppInjector } from './base/app-injector';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';

//App
import { AppComponent } from './app.component';

// Locale for fi and se
import { APP_BASE_HREF, CommonModule, LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import localeFi from '@angular/common/locales/fi';
import localeSe from '@angular/common/locales/se';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { RouterModule } from '@angular/router';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AlertModule } from './components/alert/alert.module';
import { AppRoutes } from './app.routes';
import { TenantInterceptor } from './_helpers/tenant-interceptor';
import { JwtInterceptor } from './_helpers/jwt-interceptor';
import { RequestInterceptor } from './_helpers/request.interceptor';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { LoginComponent } from './components/login/login-form/login-form.component';
import { TenantSelectComponent } from './components/login/tenant-select/tenant-select.component';
import { ForgotPasswordComponent } from './components/login/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './components/login/reset-password/reset-password.component';
import { AcceptOrgInvitationComponent } from './areas/organization/accept-org-invite/accept-org-invitation.component';
import { IndexComponent } from './components/layout/index/index.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { BreadcrumbComponent } from './components/layout/breadcrumb/breadcrumb.component';
import { LogOutModalComponent } from './components/login/log-out-modal/log-out-modal.component';
import { TenantComponent } from './components/layout/tenant/tenant.component';
import { NotificationListModule } from './areas/notification/components/notification-list/notification-list.module';
import { CancelInterceptor } from './_helpers/cancel.interceptor';
import { ServerConfiguration } from '@services/configuration.service';
import { KupariInputModule } from './components/input/kupari-input/kupari-input.module';
import { KupariButtonModule } from './components/kupari-button/kupari-button.module';
import { KupariColumnSelectorModule } from './components/kupari-column-selector/kupari-column-selector.module';
import { KupariListModule } from './components/kupari-list/kupari-list.module';
import { KupariLabelModule } from './components/input/kupari-label/kupari-label.module';
import { AssingmentQuickLinkComponent } from './components/search/assingment-quick-link/assingment-quick-link.component';
import { NoAccessComponent } from './components/no-access/no-access.component';
import { SideNavModule } from './components/layout/side-nav/side-nav.module';
import { ThankYouComponent } from './components/thank-you/thank-you.component';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { AppState } from './_store/app.state';
import { environment } from '@environments/environment';
import { ListModelState } from '@app/_store/list-model/list-model.state';
import { ListComponentState } from '@app/_store/list-component/list-component.state';
import { ErrorComponent } from './components/error/error.component';
import { ListModelStateHelpers } from './_store/list-model/list-model.helpers';
import { ModulesState } from './_store/modules/modules.state';
import { NotificationState } from './_store/modules/areas/notification/notification.state';
import { LoginFooterComponent } from './components/login/login-footer/login-footer.component';
import { LanguageSelectComponent } from './components/language-select/language-select.component';
import { TenantSelectDropMenuComponent } from './components/tenant-select-dropmenu/tenant-select-dropmenu.component';
import { WorkCalendarState } from './_store/modules/areas/work-calendar/work-calendar.state';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { ServiceWorkerModule } from '@angular/service-worker';
import { OAuthModule } from './components/oauth/oauth.module';
import { WorkTimeIndicatorModule } from './areas/work-time/components/work-time-indicator/work-time-indicator.module';
import { KupariMultiBoxInputModule } from './components/input/kupari-multi-box-input/kupari-multi-box-input.module';
import { UpdateStatusComponent } from './components/update-status/update-status.component';

registerLocaleData(localeFi);
registerLocaleData(localeSe);
registerLocaleData(localeEn);
registerLocaleData(localeSv);

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient, config: ServerConfiguration) {
  return new TranslateHttpLoader(http, '../assets/i18n/', `.json?v=${config.releaseVersion}`);
}

@NgModule({ declarations: [
        AcceptOrgInvitationComponent,
        AppComponent,
        BreadcrumbComponent,
        FooterComponent,
        ForgotPasswordComponent,
        IndexComponent,
        LoginComponent,
        LogOutModalComponent,
        NavMenuComponent,
        ResetPasswordComponent,
        TenantComponent,
        TenantSelectComponent,
        AssingmentQuickLinkComponent,
        NoAccessComponent,
        ThankYouComponent,
        ErrorComponent,
        LoginFooterComponent,
        LanguageSelectComponent,
        UpdateStatusComponent,
        TenantSelectDropMenuComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        BrowserAnimationsModule,
        RouterModule.forRoot(AppRoutes, { useHash: false, paramsInheritanceStrategy: 'always' }),
        CommonModule,
        ReactiveFormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient, ServerConfiguration]
            }
        }),
        OAuthModule,
        LoadingBarHttpClientModule,
        BsDropdownModule.forRoot(),
        ModalModule.forRoot(),
        AlertModule,
        WorkTimeIndicatorModule,
        NotificationListModule, // For notification list in navigation
        KupariInputModule,
        KupariMultiBoxInputModule,
        KupariButtonModule,
        KupariListModule,
        KupariColumnSelectorModule,
        KupariLabelModule,
        SideNavModule,
        NgxsModule.forRoot([
            AppState,
            ListModelState,
            ListComponentState,
            ModulesState,
            NotificationState,
            WorkCalendarState
        ], { developmentMode: !environment.production }),
        NgxsReduxDevtoolsPluginModule.forRoot({ name: 'avux' }),
        ProgressbarModule.forRoot(),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [

        provideNgxWebstorage(
          withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
          withLocalStorage(),
          withSessionStorage()
        ),
        // { provide: HTTP_INTERCEPTORS, useClass: CancelInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
        { provide: LOCALE_ID, useValue: 'fi' },
        ListModelStateHelpers,
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: APP_BASE_HREF, useValue: '/' },
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    ] })
export class AppModule {
  constructor(injector: Injector) {
    // //Store module's injector. Used for dependency injection.
    AppInjector.setInjector(injector);
  }
}
